import styles from './statistics-styles.module.scss';

import { Theme, useMediaQuery } from '@mui/material';
import { ColumnDef, useReactTable } from '@tanstack/react-table';
import { getSortedRowModel } from '@tanstack/table-core';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StatisticSalesVolumeYear } from '@work4all/models/lib/Classes/StatisticSalesVolumeYear.entity';
import { StatisticSalesVolumeYearItem } from '@work4all/models/lib/Classes/StatisticSalesVolumeYearItem.entity';

import { ICssClasses } from '../types';
import { CurrencyCell } from '../utils';
import { useDefaultReactTableOptions } from '../utils/use-default-react-table-options';
import { VirtualizedTableV8 } from '../VirtualizedTableV8';

import { getStatisticFormatedDate } from './getStatisticFormatedDate';

interface StatisticSalesVolumeYearTableProps {
  data: StatisticSalesVolumeYear[];
  tableClasses?: ICssClasses;
}

const GetTotalSum = (yearItem: StatisticSalesVolumeYearItem[]) => {
  let result = 0;
  yearItem?.forEach((item: StatisticSalesVolumeYearItem) => {
    result += item.amount;
  });
  return result;
};

const GetTotalMonthValue = (
  data: StatisticSalesVolumeYear[],
  month: number
) => {
  let result = 0;

  data.forEach((row) => {
    result += row.items[month].amount;
  });

  return (
    <CurrencyCell
      className={clsx({ [styles.red]: result < 0 })}
      value={result}
      maximumFractionDigits={0}
      minimumFractionDigits={0}
      hideUnit={false}
    />
  );
};

const GetTotalTotalSumValue = (data: StatisticSalesVolumeYear[]) => {
  let result = 0;

  data.forEach((row) => {
    result += GetTotalSum(row.items);
  });

  return (
    <CurrencyCell
      className={clsx({ [styles.red]: result < 0 })}
      value={result}
      maximumFractionDigits={0}
      minimumFractionDigits={0}
      hideUnit={false}
    />
  );
};

export const StatisticSalesVolumeYearTable: React.FC<
  StatisticSalesVolumeYearTableProps
> = (props) => {
  const { data, tableClasses } = props;

  const isViewportDownMd = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<StatisticSalesVolumeYear, any>[]>(() => {
    const result: ColumnDef<StatisticSalesVolumeYear, any>[] = [
      {
        header: t('COMMON.YEAR'),
        accessorKey: 'start',
        cell: (props) => {
          const value = props.getValue() as string;
          return DateTime.fromJSDate(new Date(value)).toFormat('yyyy');
        },
        size: 70,
      },
    ];

    result.push({
      header: t('COMMON.SUM'),
      id: 'sum',
      accessorFn: (row) => row.items,
      footer: () => GetTotalTotalSumValue(data),
      cell: (props) => {
        const value = props.getValue() as StatisticSalesVolumeYearItem[];
        if (!value) {
          return '';
        }
        const displayValue = GetTotalSum(value);
        return (
          <CurrencyCell
            className={clsx({ [styles.red]: displayValue < 0 })}
            value={displayValue}
            maximumFractionDigits={0}
            minimumFractionDigits={0}
            hideUnit={true}
          />
        );
      },
      size: isViewportDownMd ? 100 : 150,
      alignment: 'right',
    });

    data?.[0]?.items?.forEach((item: StatisticSalesVolumeYearItem, index) => {
      result.push({
        header: getStatisticFormatedDate(new Date(item.month), 'MMMM'),
        id: `items_${index}_amount`,
        accessorFn: (row) => row.items[index]?.amount,
        footer: () => GetTotalMonthValue(data, index),
        cell: (props) => {
          const value = props.getValue() as number;
          if (!value) {
            return '';
          }
          return (
            <CurrencyCell
              className={clsx({ [styles.red]: value < 0 })}
              value={parseInt(value.toFixed(0))}
              maximumFractionDigits={0}
              minimumFractionDigits={0}
              hideUnit={true}
            />
          );
        },
        size: isViewportDownMd ? 100 : 150,
        alignment: 'right',
      });
    });

    return result;
  }, [data, t, isViewportDownMd]);

  const table = useReactTable({
    ...useDefaultReactTableOptions(),
    getSortedRowModel: getSortedRowModel(),
    columns,
    data,
    enableMultiRowSelection: false,
    enableColumnResizing: false,
    entityType: null,
    initialState: {
      columnPinning: { left: ['start'] },
    },
  });

  return (
    data.length > 0 && (
      <VirtualizedTableV8
        table={table}
        fetchMore={() => null}
        classes={tableClasses}
        className={styles.table}
        displayFooter
      />
    )
  );
};
